/**
 * Device Session API
 * Manages CLI device sessions for multi-device tracking
 */

import { getApiClient } from './client.js';
import { getConfigValue, setConfigValue } from '../utils/config.js';
import { hostname } from 'os';

export interface DeviceSession {
  id: number;
  deviceId: string;
  deviceType: string;
  deviceName?: string;
  lastActiveAt: string;
  createdAt: string;
  isCurrentDevice?: boolean;
  settings?: Record<string, unknown>;
}

/**
 * Register or update the current device session
 */
export async function registerDevice(): Promise<DeviceSession | null> {
  try {
    const client = getApiClient();
    const deviceName = `CLI@${hostname()}`;

    const response = await client.post<{ success: boolean; data: DeviceSession }>('/devices', {
      deviceType: 'cli',
      deviceName,
      deviceId: getConfigValue('deviceSessionId') || `cli-${Date.now()}`,
    });

    if (response.data.success && response.data.data) {
      setConfigValue('deviceSessionId', String(response.data.data.id));
      return response.data.data;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Send a heartbeat for the current device session
 */
export async function sendHeartbeat(): Promise<boolean> {
  try {
    const client = getApiClient();
    await client.post('/devices/heartbeat');
    return true;
  } catch {
    return false;
  }
}

/**
 * List all active device sessions
 */
export async function listDevices(): Promise<DeviceSession[]> {
  const client = getApiClient();
  const response = await client.get<{ success?: boolean; data?: DeviceSession[]; devices?: DeviceSession[] }>('/devices');
  return response.data.data || response.data.devices || [];
}

/**
 * Revoke a specific device session
 */
export async function revokeDevice(deviceId: string | number): Promise<boolean> {
  try {
    const client = getApiClient();
    await client.delete(`/devices/${deviceId}`);
    return true;
  } catch {
    return false;
  }
}

/**
 * Revoke all other device sessions
 */
export async function revokeAllOtherDevices(): Promise<boolean> {
  try {
    const client = getApiClient();
    await client.delete('/devices');
    return true;
  } catch {
    return false;
  }
}
